import { AddOutlined, DeleteOutlined } from '@mui/icons-material';
import { Box, Button, IconButton, Paper, Stack, Typography } from '@mui/material';
import { FieldArray, useFormikContext } from 'formik';
import { type FC, useMemo } from 'react';

import FormField from 'components/common/formikForm/formField';

import useTranslate from 'hooks/useTranslate';
import type { ConsultantLicenseFormValues } from 'types';

interface ConsultantLicenseFieldsProps {
    descriptionKey?: string;
    showHeading?: boolean;
}

interface LicenseFormSlice {
    licenses: ConsultantLicenseFormValues[];
}

const LICENSE_COUNTRIES = [
    { labelKey: 'australia', value: 'AU' },
    { labelKey: 'canada', value: 'CA' },
    { labelKey: 'unitedKingdom', value: 'UK' },
];

const MAX_LICENSES = 10;

export const ConsultantLicenseFields: FC<ConsultantLicenseFieldsProps> = ({
    descriptionKey = 'licenseDetailsDescription',
    showHeading = true,
}) => {
    const { translate } = useTranslate();
    const { isSubmitting, values } = useFormikContext<LicenseFormSlice>();
    const countryOptions = useMemo(
        () =>
            LICENSE_COUNTRIES.map((country) => ({
                label: translate(country.labelKey),
                value: country.value,
            })),
        [translate]
    );

    return (
        <FieldArray name="licenses">
            {({ push, remove }) => (
                <Stack spacing={1.25}>
                    {showHeading ? (
                        <Box>
                            <Typography
                                variant="overline"
                                color="text.secondary"
                                sx={{ fontWeight: 700, letterSpacing: '0.08em' }}
                            >
                                {translate('licenseDetails')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {translate(descriptionKey)}
                            </Typography>
                        </Box>
                    ) : null}

                    {values.licenses.map((_license, index) => (
                        <Paper
                            key={values.licenses[index].id ?? `new-${index}`}
                            variant="outlined"
                            sx={{
                                p: 1.5,
                                borderRadius: 1.5,
                                bgcolor: 'rgba(248, 250, 252, 0.55)',
                            }}
                        >
                            <Stack spacing={1}>
                                <Stack
                                    direction="row"
                                    sx={{
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        {translate('license')} {index + 1}
                                    </Typography>
                                    {values.licenses.length > 1 ? (
                                        <IconButton
                                            size="small"
                                            disabled={isSubmitting}
                                            aria-label={`${translate('removeLicense')} ${index + 1}`}
                                            onClick={() => remove(index)}
                                        >
                                            <DeleteOutlined sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    ) : null}
                                </Stack>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        gap: 1.5,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: {
                                                xs: '100%',
                                                sm: 'calc((100% - 24px) * 3 / 12 + 8px)',
                                            },
                                            minWidth: 0,
                                            flexShrink: 0,
                                        }}
                                    >
                                        <FormField
                                            required
                                            type="select"
                                            name={`licenses.${index}.country`}
                                            label="country"
                                            options={countryOptions}
                                            disabled={isSubmitting}
                                        />
                                    </Box>
                                    <Box
                                        sx={{
                                            width: {
                                                xs: '100%',
                                                sm: 'calc((100% - 24px) * 4 / 12)',
                                            },
                                            minWidth: 0,
                                            flexShrink: 0,
                                        }}
                                    >
                                        <FormField
                                            required
                                            name={`licenses.${index}.license_number`}
                                            label="licenseNumber"
                                            disabled={isSubmitting}
                                        />
                                    </Box>
                                    <Box
                                        sx={{
                                            width: {
                                                xs: '100%',
                                                sm: 'calc((100% - 24px) * 5 / 12 - 8px)',
                                            },
                                            minWidth: 0,
                                            flexShrink: 0,
                                            '& .MuiPickersInputBase-sectionsContainer': {
                                                width: 'auto',
                                                minWidth: 0,
                                                maxWidth: '100%',
                                            },
                                        }}
                                    >
                                        <FormField
                                            required
                                            maxDaysFromToday
                                            type="date"
                                            name={`licenses.${index}.date_issued`}
                                            label="dateIssued"
                                            disabled={isSubmitting}
                                        />
                                    </Box>
                                </Box>
                            </Stack>
                        </Paper>
                    ))}

                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddOutlined />}
                        disabled={isSubmitting || values.licenses.length >= MAX_LICENSES}
                        onClick={() =>
                            push({
                                country: '',
                                license_number: '',
                                date_issued: '',
                            })
                        }
                        sx={{ alignSelf: 'flex-start' }}
                    >
                        {translate('addLicense')}
                    </Button>
                </Stack>
            )}
        </FieldArray>
    );
};
